import React, { useReducer, useEffect, useRef, lazy, Suspense } from 'react';
import {
    Box, Grid, TextField, Button, Typography,
    CircularProgress, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Tooltip,
    Checkbox, ListItemText
} from '@mui/material';
import Chip from '@mui/material/Chip';
import { Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useEvent } from '../../contexts/event.context'
import * as qs from 'qs';

// Lazy loading dos componentes
const ParticipantsTable = React.lazy(() => import('../../components/ParticipantsTable'));
const Autocomplete = lazy(() => import('@mui/material/Autocomplete'));

// Estado inicial
const initialState = {
    selectedEvent: '',
    selectedCategory: [],
    selectedSource: 'inscritos',
    subscribers: [],
    allPresentes: [],
    allSelectedSubscribers: new Map(),
    selectedSubscribers: new Set(),
    loading: false,
    events: [],
    availableCategories: [],
    pagination: {
        page: 1,
        pageSize: 200,
        total: 0,
        pageCount: 0
    },
    stateAlert: {
        status: false,
        type: 'success',
        msg: ''
    }
};

// Reducer
function sortedConfigReducer(state, action) {
    switch (action.type) {
        case 'SET_SELECTED_EVENT':
            return {
                ...state,
                selectedEvent: action.payload,
                selectedCategory: [],
                subscribers: [],
                selectedSubscribers: new Set(),
                allSelectedSubscribers: new Map(),
                pagination: { ...state.pagination, page: 1 }
            };
        case 'SET_EVENTS':
            return { ...state, events: action.payload };

        case 'SET_SELECTED_SOURCE':
            return {
                ...state,
                selectedSource: action.payload,
                selectedCategory: [],
                subscribers: [],
                selectedSubscribers: new Set(),
                allSelectedSubscribers: new Map(),
                allPresentes: [],
                pagination: { ...state.pagination, page: 1 }
            };

        case 'SET_SELECTED_CATEGORY':
            return {
                ...state,
                selectedCategory: action.payload,
                pagination: { ...state.pagination, page: 1 }
            };

        case 'SET_LOADING':
            return { ...state, loading: action.payload };

        case 'SET_SUBSCRIBERS':
            return { ...state, subscribers: action.payload };

        case 'SET_ALL_PRESENTES':
            return { ...state, allPresentes: action.payload };

        case 'UPDATE_CURRENT_PAGE_SELECTION':
            const currentPageSelection = new Set();
            state.subscribers.forEach(subscriber => {
                if (state.allSelectedSubscribers.has(subscriber.id)) {
                    currentPageSelection.add(subscriber.id);
                }
            });
            return { ...state, selectedSubscribers: currentPageSelection };

        case 'SET_AVAILABLE_CATEGORIES':
            return { ...state, availableCategories: action.payload };

        case 'SET_PAGINATION':
            return { ...state, pagination: { ...state.pagination, ...action.payload } };

        case 'TOGGLE_SUBSCRIBER':
            const newSelected = new Set(state.selectedSubscribers);
            const newAllSelected = new Map(state.allSelectedSubscribers);
            const subscriber = state.subscribers.find(sub => sub.id === action.payload);

            if (newSelected.has(action.payload)) {
                newSelected.delete(action.payload);
                newAllSelected.delete(action.payload);
            } else {
                newSelected.add(action.payload);
                if (subscriber) {
                    newAllSelected.set(action.payload, subscriber);
                }
            }
            return {
                ...state,
                selectedSubscribers: newSelected,
                allSelectedSubscribers: newAllSelected
            };

        case 'SELECT_ALL_SUBSCRIBERS':
            const allIds = new Set(state.subscribers.map(sub => sub.id));
            const newAllSelectedForSelectAll = new Map(state.allSelectedSubscribers);

            state.subscribers.forEach(subscriber => {
                newAllSelectedForSelectAll.set(subscriber.id, subscriber);
            });

            return {
                ...state,
                selectedSubscribers: allIds,
                allSelectedSubscribers: newAllSelectedForSelectAll
            };

        case 'SELECT_ALL_FROM_LOAD':
            const allLoadedSubscribers = new Map(state.allSelectedSubscribers);
            action.payload.forEach(subscriber => {
                allLoadedSubscribers.set(subscriber.id, subscriber);
            });

            const currentPageIds = new Set(state.subscribers.map(sub => sub.id));

            return {
                ...state,
                selectedSubscribers: currentPageIds,
                allSelectedSubscribers: allLoadedSubscribers
            };

        case 'DESELECT_ALL_SUBSCRIBERS':
            const newSelectedForDeselect = new Set(state.selectedSubscribers);
            const newAllSelectedForDeselect = new Map(state.allSelectedSubscribers);

            state.subscribers.forEach(subscriber => {
                newSelectedForDeselect.delete(subscriber.id);
                newAllSelectedForDeselect.delete(subscriber.id);
            });

            return {
                ...state,
                selectedSubscribers: newSelectedForDeselect,
                allSelectedSubscribers: newAllSelectedForDeselect
            };

        case 'CLEAR_ALL_SELECTIONS':
            return {
                ...state,
                selectedSubscribers: new Set(),
                allSelectedSubscribers: new Map()
            };

        case 'SET_ALERT':
            return { ...state, stateAlert: action.payload };

        case 'CLOSE_ALERT':
            return { ...state, stateAlert: { ...state.stateAlert, status: false } };

        default:
            return state;
    }
}

export default function SortedConfig() {
    const [state, dispatch] = useReducer(sortedConfigReducer, initialState);
    const { events, getEvents, loading: eventsLoading, pagination: eventsPagination } = useEvent();
    const query = useRef();
    const navigate = useNavigate();

    // Carrega todas as categorias disponíveis para o evento
    const loadAvailableCategories = async () => {
        if (!state.selectedEvent) return;

        try {
            const selectedEventData = state.events.find(e => e.attributes?.name === state.selectedEvent);

            if (selectedEventData && selectedEventData.attributes?.plans?.data) {
                const categories = selectedEventData.attributes.plans.data
                    .map(plan => plan.attributes?.type)
                    .filter(Boolean);

                const uniqueCategories = [...new Set(categories)];
                dispatch({ type: 'SET_AVAILABLE_CATEGORIES', payload: uniqueCategories });
            } else {
                const params = {
                    filters: {
                        name: { $eq: state.selectedEvent }
                    },
                    populate: {
                        plans: {
                            fields: ['type']
                        }
                    }
                };

                query.current = qs.stringify(params);
                const response = await api.get(`/events?${query.current}`);

                if (response.data.data.length > 0) {
                    const eventPlans = response.data.data[0].attributes?.plans?.data || [];
                    const categories = eventPlans
                        .map(plan => plan.attributes?.type)
                        .filter(Boolean);

                    const uniqueCategories = [...new Set(categories)];
                    dispatch({ type: 'SET_AVAILABLE_CATEGORIES', payload: uniqueCategories });
                }
            }
        } catch (error) {
            console.error('Erro ao carregar categorias:', error);
        }
    };

    // Carrega subscribers do evento selecionado
    const loadSubscribers = async (page = 1) => {
        if (!state.selectedEvent) return;

        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            if (state.selectedSource === 'presentes') {
                await loadPresentes(page);
            } else {
                await loadInscritos(page);
            }
        } catch (error) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || 'Erro ao carregar dados'
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Carrega inscritos do evento
    const loadInscritos = async (page = 1) => {
        const params = {
            sort: ['createdAt:desc'],
            pagination: { page, pageSize: state.pagination.pageSize },
            filters: {
                deleted: { $eq: false },
                event: { name: { $contains: state.selectedEvent } },
                ...(state.selectedCategory.length > 0 && {
                    plan: { type: { $in: state.selectedCategory } }
                })
            },
            populate: {
                subscribers: {
                    fields: ['document', 'name', 'email', 'state', 'city']
                },
                company: {
                    fields: ['social_reason', 'state', 'city']
                },
                plan: {
                    fields: ['type']
                }
            }
        };

        query.current = qs.stringify(params);
        const response = await api.get(`/orders/?${query.current}`);

        const flattenedSubscribers = response.data.data.flatMap(order => {
            const subscribers = order.attributes.subscribers?.data || [];
            return subscribers.map(subscriber => {
                const companyCity = order.attributes.company?.data?.attributes?.city;
                const companyState = order.attributes.company?.data?.attributes?.state;

                return {
                    id: `${order.id}-${subscriber.id}`,
                    orderId: order.id,
                    subscriberId: subscriber.id,
                    name: subscriber.attributes.name,
                    email: subscriber.attributes.email,
                    document: subscriber.attributes.document,
                    company: order.attributes.company?.data?.attributes?.social_reason || '',
                    city: companyCity || subscriber.attributes?.city || '',
                    state: companyState || subscriber.attributes?.state || '',
                    category: order.attributes.plan?.data?.attributes?.type || ''
                };
            });
        });

        dispatch({ type: 'SET_SUBSCRIBERS', payload: flattenedSubscribers });
        dispatch({ type: 'UPDATE_CURRENT_PAGE_SELECTION' });
        dispatch({
            type: 'SET_PAGINATION',
            payload: {
                page: response.data.meta.pagination.page,
                total: response.data.meta.pagination.total,
                pageCount: response.data.meta.pagination.pageCount
            }
        });
    };

    // Carrega TODOS os inscritos do evento
    const loadAllInscritos = async () => {
        if (!state.selectedEvent) return;

        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            const allSubscribers = [];
            let currentPage = 1;
            let hasMorePages = true;

            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'info',
                    msg: 'Carregando todos os inscritos... Isso pode levar alguns segundos.'
                }
            });

            while (hasMorePages) {
                const params = {
                    sort: ['createdAt:desc'],
                    pagination: { page: currentPage, pageSize: 100 },
                    filters: {
                        deleted: { $eq: false },
                        event: { name: { $contains: state.selectedEvent } },
                        ...(state.selectedCategory.length > 0 && {
                            plan: { type: { $in: state.selectedCategory } }
                        })
                    },
                    populate: {
                        subscribers: {
                            fields: ['document', 'name', 'email']
                        },
                        company: {
                            fields: ['social_reason', 'state', 'city']
                        },
                        plan: {
                            fields: ['type']
                        }
                    }
                };

                query.current = qs.stringify(params);
                const response = await api.get(`/orders/?${query.current}`);

                const flattenedSubscribers = response.data.data.flatMap(order => {
                    const subscribers = order.attributes.subscribers?.data || [];
                    return subscribers.map(subscriber => {
                        const companyCity = order.attributes.company?.data?.attributes?.city;
                        const companyState = order.attributes.company?.data?.attributes?.state;

                        return {
                            id: `${order.id}-${subscriber.id}`,
                            orderId: order.id,
                            subscriberId: subscriber.id,
                            name: subscriber.attributes.name,
                            email: subscriber.attributes.email,
                            document: subscriber.attributes.document,
                            company: order.attributes.company?.data?.attributes?.social_reason || '',
                            city: companyCity || subscriber.attributes?.city || '',
                            state: companyState || subscriber.attributes?.state || '',
                            category: order.attributes.plan?.data?.attributes?.type || ''
                        };
                    });
                });

                allSubscribers.push(...flattenedSubscribers);
                hasMorePages = currentPage < response.data.meta.pagination.pageCount;
                currentPage++;
            }

            dispatch({
                type: 'SELECT_ALL_FROM_LOAD',
                payload: allSubscribers
            });

            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'success',
                    msg: `✅ Todos os ${allSubscribers.length} inscritos foram selecionados!`
                }
            });

        } catch (error) {
            console.error('Erro ao carregar todos os inscritos:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Erro ao carregar todos os inscritos'
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Carrega presentes do evento
    const loadPresentes = async (page = 1) => {
        if (state.allPresentes.length > 0) {
            applyPresentesFilter(page);
            return;
        }

        const params = {
            sort: ['readAt:desc'],
            pagination: {
                page: 1,
                pageSize: 200
            },
            filters: {
                event: {
                    name: { $eq: state.selectedEvent }
                },
            },
            populate: {
                subscriber: {
                    fields: ['document', 'name', 'email', 'badge']
                }
            }
        };

        query.current = qs.stringify(params);
        const response = await api.get(`/frequencies/?${query.current}`);

        const processedPresentes = await Promise.all(
            response.data.data.map(async (frequency) => {
                const subscriber = frequency.attributes.subscriber.data;
                let company = '';
                let city = '';
                let state = '';
                let category = '';

                try {
                    const orderParams = {
                        filters: {
                            deleted: { $eq: false },
                            event: { name: { $contains: state.selectedEvent } },
                            subscribers: { id: { $eq: subscriber.id } }
                        },
                        populate: {
                            company: {
                                fields: ['social_reason', 'state', 'city']
                            },
                            plan: {
                                fields: ['type']
                            }
                        }
                    };

                    const orderQuery = qs.stringify(orderParams);
                    const orderResponse = await api.get(`/orders/?${orderQuery}`);

                    if (orderResponse.data.data.length > 0) {
                        const order = orderResponse.data.data[0];
                        const companyCity = order.attributes.company?.data?.attributes?.city;
                        const companyState = order.attributes.company?.data?.attributes?.state;

                        company = order.attributes.company?.data?.attributes?.social_reason || '';
                        city = companyCity || subscriber.attributes?.city || '';
                        state = companyState || subscriber.attributes?.state || '';
                        category = order.attributes.plan?.data?.attributes?.type || '';
                    }
                } catch (error) {
                    console.warn(`Erro ao buscar pedido para subscriber ${subscriber.id}:`, error);
                }

                return {
                    id: `freq-${frequency.id}-${subscriber.id}`,
                    frequencyId: frequency.id,
                    subscriberId: subscriber.id,
                    name: subscriber.attributes.name,
                    email: subscriber.attributes.email,
                    document: subscriber.attributes.document,
                    badge: subscriber.attributes.badge,
                    company,
                    city,
                    state,
                    category,
                    readAt: frequency.attributes.readAt,
                    order: frequency.attributes.order || ''
                };
            })
        );

        dispatch({ type: 'SET_ALL_PRESENTES', payload: processedPresentes });
        applyPresentesFilter(page, processedPresentes);
    };

    // Aplica filtro de categoria e paginação nos presentes
    const applyPresentesFilter = (page = 1, presentes = null) => {
        const dataToFilter = presentes || state.allPresentes;

        let filteredPresentes = dataToFilter;
        if (state.selectedCategory.length > 0) {
            filteredPresentes = dataToFilter.filter(p => state.selectedCategory.includes(p.category));
        }

        const startIndex = (page - 1) * state.pagination.pageSize;
        const endIndex = startIndex + state.pagination.pageSize;
        const paginatedPresentes = filteredPresentes.slice(startIndex, endIndex);

        dispatch({ type: 'SET_SUBSCRIBERS', payload: paginatedPresentes });
        dispatch({ type: 'UPDATE_CURRENT_PAGE_SELECTION' });
        dispatch({
            type: 'SET_PAGINATION',
            payload: {
                page: page,
                total: filteredPresentes.length,
                pageCount: Math.ceil(filteredPresentes.length / state.pagination.pageSize)
            }
        });
    };

    // Handlers
    const handleEventChange = (event, newValue) => {
        if (newValue && newValue.attributes) {
            dispatch({ type: 'SET_SELECTED_EVENT', payload: newValue.attributes.name });
        } else {
            dispatch({ type: 'SET_SELECTED_EVENT', payload: '' });
        }
    };

    const handleSourceChange = (event) => {
        dispatch({ type: 'SET_SELECTED_SOURCE', payload: event.target.value });
    };

    const handleCategoryChange = (event) => {
        const value = event.target.value;
        dispatch({ type: 'SET_SELECTED_CATEGORY', payload: typeof value === 'string' ? value.split(',') : value });
    };

    const handleSelectAll = () => {
        dispatch({ type: 'SELECT_ALL_SUBSCRIBERS' });
    };

    const handlePageChange = (page) => {
        loadSubscribers(page);
    };

    const handleGoToSorteio = () => {
        // Armazena a configuração no localStorage
        const config = {
            selectedEvent: state.selectedEvent,
            selectedCategory: state.selectedCategory,
            selectedSource: state.selectedSource,
            allSelectedSubscribers: Array.from(state.allSelectedSubscribers.entries())
        };
        localStorage.setItem('sortedConfig', JSON.stringify(config));
        navigate('/SorteioRealizacao');
    };

    useEffect(() => {
        if (state.selectedEvent) {
            loadAvailableCategories();
            loadSubscribers(1);
        }
    }, [state.selectedEvent]);

    useEffect(() => {
        if (state.selectedEvent) {
            loadSubscribers(1);
        }
    }, [state.selectedSource, state.selectedCategory]);

    useEffect(() => {
        if (!eventsLoading && events) {
            dispatch({ type: 'SET_EVENTS', payload: [...events] });
        }
    }, [events, eventsLoading]);

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Snackbar
                open={state.stateAlert.status}
                autoHideDuration={6000}
                onClose={() => dispatch({ type: 'CLOSE_ALERT' })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={state.stateAlert.type} onClose={() => dispatch({ type: 'CLOSE_ALERT' })}>
                    {state.stateAlert.msg}
                </Alert>
            </Snackbar>

            <Typography variant="h4" gutterBottom>
                Configuração do Sorteio
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                Selecione o evento, categoria e participantes para o sorteio
            </Typography>

            <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <Suspense fallback={<CircularProgress />}>
                            <Autocomplete
                                fullWidth
                                options={state.events}
                                getOptionLabel={(option) => option.attributes?.name || ''}
                                value={state.events.find(e => e.attributes?.name === state.selectedEvent) || null}
                                onChange={handleEventChange}
                                loading={eventsLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Selecionar Evento"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {eventsLoading && <CircularProgress color="inherit" size={20} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Suspense>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth>
                            <InputLabel>Origem</InputLabel>
                            <Select
                                value={state.selectedSource}
                                onChange={handleSourceChange}
                                label="Origem"
                                disabled={!state.selectedEvent}
                            >
                                <MenuItem value="inscritos">📝 Inscritos</MenuItem>
                                <MenuItem value="presentes">✅ Presentes</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                            <InputLabel>Categorias</InputLabel>
                            <Select
                                multiple
                                value={state.selectedCategory}
                                onChange={handleCategoryChange}
                                label="Categorias"
                                disabled={!state.selectedEvent}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.length === 0 ? (
                                            <em>Todas as categorias</em>
                                        ) : (
                                            selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))
                                        )}
                                    </Box>
                                )}
                            >
                                {state.availableCategories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        <Checkbox checked={state.selectedCategory.indexOf(category) > -1} />
                                        <ListItemText primary={category} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => loadSubscribers(1)}
                            disabled={!state.selectedEvent}
                            startIcon={<Refresh />}
                        >
                            Atualizar
                        </Button>
                    </Grid>
                </Grid>

                {state.selectedEvent && (
                    <>
                        <Box mt={2} mb={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Tooltip title="Carrega e seleciona TODOS os inscritos do evento">
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            color="primary"
                                            onClick={loadAllInscritos}
                                            disabled={state.loading || state.selectedSource !== 'inscritos'}
                                            startIcon={state.loading ? <CircularProgress size={20} /> : null}
                                        >
                                            {state.loading ? 'Carregando...' : 'Selecionar TODOS os Inscritos'}
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={4} sm={3} md={4}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        💡 Total selecionado: <strong>{state.allSelectedSubscribers.size}</strong> participante(s) |
                                        Página atual: <strong>{state.selectedSubscribers.size}/{state.subscribers.length}</strong>
                                    </Typography>
                                </Grid>
                                {state.allSelectedSubscribers.size > 0 && (
                                    <Grid item xs={4} sm={6} md={4}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="large"
                                            onClick={handleGoToSorteio}
                                        >
                                            Ir para o Sorteio ({state.allSelectedSubscribers.size} selecionados)
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        <Suspense fallback={<CircularProgress />}>
                            <ParticipantsTable
                                subscribers={state.subscribers}
                                selectedSubscribers={state.selectedSubscribers}
                                allSelectedSubscribers={state.allSelectedSubscribers}
                                loading={state.loading}
                                pagination={state.pagination}
                                onToggleSubscriber={(id) => dispatch({ type: 'TOGGLE_SUBSCRIBER', payload: id })}
                                onSelectAll={handleSelectAll}
                                onClearAll={() => dispatch({ type: 'CLEAR_ALL_SELECTIONS' })}
                                onPageChange={handlePageChange}
                            />
                        </Suspense>


                    </>
                )}
            </Box>
        </Box>
    );
}
